import type { RunVisualViewId } from "@/api/run-visuals";
import type { RunVisualAvailability, RunVisualView } from "@/components/runs/run-visual-views";

/**
 * The run workspace's MAIN-AREA MODE, and the side panel that pairs with it
 * (flowstack#991).
 *
 * The workspace used to be a fixed split: the DAG on the left, always, with a
 * 26rem panel on the right carrying whatever visual the run had produced. That
 * shape gave the graph the whole main area and left every *visual* surface in
 * a column too narrow to read — three ~90px torsion cards, or a 3D structure
 * in a box smaller than the tile-settings list beneath it.
 *
 * The operator's model (#991, second comment) replaces it: **the main area is
 * a mode, and the side panel follows the mode.** The user picks what the main
 * area shows; whatever that is gets the full width the DAG used to monopolise,
 * and the side panel is then always the DETAIL of that thing rather than a
 * generic dumping ground.
 *
 * **VIS/V6 makes the rail a STACK.** #991's table paired each mode with ONE
 * panel, which meant tile settings and the molecule could never be on screen
 * together: selecting a step in the graph replaced the structure, and getting
 * it back meant leaving the graph. The pairing survives — what changes is that
 * the rail is a list, and a selected step appends to it instead of taking it
 * over.
 *
 * | Main area     | Rail, top to bottom                              |
 * |---------------|--------------------------------------------------|
 * | DAG           | the molecule · tile settings                     |
 * | Torsion plots | the scanned angle's structure · the fit's numbers |
 * | Molecule      | the fragment list and its fit numbers            |
 *
 * ...plus **tile settings appended to any of them while a step is selected**.
 * The last two rows are the same pairing inverted: structure beside numbers,
 * with whichever one you came to read taking the main area.
 *
 * Kept as pure functions over the visuals index (like `run-visual-views.ts`,
 * whose availability rules this reuses rather than restates) so the mode
 * table is testable without mounting reactflow, a chart or a 3D viewer.
 */

/** The main area's mode. `dag` plus every view a run can visualize. */
export type RunWorkspaceModeId = "dag" | RunVisualViewId;

/**
 * One SECTION of the rail beside the main area.
 *
 * Plural since VIS/V6: the rail carries an ordered list of these rather than
 * exactly one ({@link sidePanelsFor}).
 */
export type RunWorkspaceSideId = "tile" | "molecule" | "structure" | "fragments";

export interface RunWorkspaceMode {
  id: RunWorkspaceModeId;
  label: string;
  availability: RunVisualAvailability;
  /** The visual view backing this mode; `null` for the DAG. */
  view: RunVisualView | null;
}

/**
 * DAG first, then the visual views in their existing order.
 *
 * Spliced onto `RUN_VISUAL_VIEW_ORDER` rather than re-listed, so the visual
 * modes cannot drift out of step with the order the rest of the app uses.
 */
const DAG_LABEL = "DAG";

/**
 * Build the mode list.
 *
 * The DAG is unconditionally `ready`: this workspace only renders for a run
 * that HAS a flow graph (`RunWorkspace` bails to an empty state otherwise), so
 * there is no state in which the graph is pending or absent. Every other mode
 * inherits its availability from `deriveRunVisualViews` — the same three-state
 * ready/pending/absent rule the old selector used, unchanged: a mode whose data
 * has not arrived yet is offered-but-disabled and says *pending*, and once the
 * run is terminal it says the run produced none. Neither is ever silently
 * hidden, because a hidden mode reads as "this portal cannot do that".
 */
export function deriveWorkspaceModes(views: readonly RunVisualView[]): RunWorkspaceMode[] {
  return [
    { id: "dag" as const, label: DAG_LABEL, availability: "ready" as const, view: null },
    ...views.map((view) => ({
      id: view.id,
      label: view.label,
      availability: view.availability,
      view,
    })),
  ];
}

/**
 * The mode to show, given the user's pick.
 *
 * Falls back to the DAG rather than to "the first ready mode": the DAG is the
 * only mode guaranteed to have something to draw, and it is what the tab
 * showed before a user picked anything. A run whose torsion plots were
 * superseded by a resume therefore lands back on the graph, never on a mode
 * whose data has gone.
 */
export function resolveWorkspaceMode(
  modes: readonly RunWorkspaceMode[],
  picked: RunWorkspaceModeId | null
): RunWorkspaceMode {
  const chosen = modes.find((m) => m.id === picked && m.availability === "ready");
  // By ID, not `modes[0]` (Rupert PR-999 r1): the fallback IS "the DAG", and
  // spelling it as a position makes that true only for as long as
  // `deriveWorkspaceModes` happens to emit the DAG first. A reorder — or a
  // caller assembling its own `modes` array — would silently turn the
  // guarantee into "whatever is first" with nothing to catch it.
  return chosen ?? modes.find((m) => m.id === "dag") ?? modes[0];
}

/**
 * One entry of a mode's rail: which section, and whether the main pane can be
 * read WITHOUT it.
 *
 * The second half is flowstack#1422. Per-pane maximise (`RunWorkspace.tsx`,
 * VIZ U5.3) rests on the rail being SUPPLEMENTARY to the main pane — commentary
 * you can stand down while you read the subject full-window. That is true of
 * every section the rail carried when U5.3 landed, and it is false of exactly
 * one that arrived after it: the torsion mode's `structure` is not commentary
 * about the curve, it is the other half of the same reading. A scan point
 * without its geometry is a number.
 *
 * So separability is declared HERE, beside the pairing it qualifies, rather
 * than inferred at the maximise call site. Two consequences of putting it in
 * this table rather than in a second one keyed by mode: a section cannot be
 * marked inseparable for a mode whose rail does not carry it, and the next
 * scientific visual answers the question when it declares its rail instead of
 * inheriting "supplementary" by silence.
 */
interface RunWorkspaceRailSection {
  readonly id: RunWorkspaceSideId;
  /**
   * **This section stays on screen while the MAIN pane holds the frame.**
   *
   * Default `false`, and the default is the claim U5.3 already makes: a
   * maximised reading pane is the whole window because whichever one you came
   * for should not get a share of it. Mark a section here only where the main
   * pane's content is not READABLE without it — not merely where it is nice to
   * have beside it, since marking every section is whole-workspace maximise
   * wearing a new name, which #991 removed for a reason that still holds.
   */
  readonly inseparable?: boolean;
}

/**
 * The sections the rail carries for a mode — the whole of #991's "side panel
 * dependent", **now a STACK rather than a single slot (VIS/V6)**.
 *
 * Exhaustive over `RunWorkspaceModeId` by construction (a `Record`, not a
 * switch with a default), so adding a mode without deciding its rail is a type
 * error rather than a silent fallback to the tile settings.
 *
 * The order is the artboard's (`dev/design/flowstack-workspace/Main.dc.html`,
 * in the project-manager tree): the structure sits at the TOP of the rail and
 * the numbers about it come underneath. That ordering is the reason a
 * selection appends rather than prepends — see {@link sidePanelsFor}.
 */
const SIDE_BY_MODE: Record<RunWorkspaceModeId, readonly RunWorkspaceRailSection[]> = {
  // The graph pairs with the tile settings as it always did — and now ALSO
  // keeps the structure, which is the whole of VIS/V6's complaint: clicking a
  // step used to cost you the molecule.
  //
  // NEITHER is marked inseparable, and that is not an oversight: the DAG's
  // whole rail already survives a maximised graph, for a different reason and
  // through a different rule ({@link railSurvivesMaximisedMain} — the rail is
  // the readout of the click, not the other half of a reading). Marking them
  // here as well would state one behaviour twice and let the two copies
  // disagree.
  dag: [{ id: "molecule" }, { id: "tile" }],
  // Reading a fit: the curve is the main event, and the reference beside it is
  // the conformer AT THE SCANNED ANGLE — not the run's whole ligand (VIS/V9).
  // The two are different claims, and only one of them changes as the scrub
  // moves: pairing a curve with a structure that is the same picture at every
  // point is two panels that happen to be adjacent, which is what the operator
  // reported. The whole ligand has not gone anywhere — it is the `molecule`
  // MODE, one click away in the switch above the pane — but it does not
  // outrank the point under the cursor for this rail.
  //
  // Split into two sections rather than one panel holding both, so each is its
  // own container with its own maximise.
  //
  // **`structure` is INSEPARABLE and `fragments` is not** (flowstack#1422), and
  // the asymmetry is the point — marking both would be the whole workspace
  // going full-screen again. The structure is the conformer AT THE SCRUBBED
  // ANGLE: it changes as the point under the cursor moves, so it is the other
  // half of what the curve says rather than a note about it. The fit numbers
  // are the note — one fragment's RMSE and barrier figures, the same three
  // values at every point of the scan — and a reader who maximises the curve to
  // count its minima is not losing an argument by not having them beside it.
  torsion: [{ id: "structure", inseparable: true }, { id: "fragments" }],
  // ...and inverted. Reading a structure, the fit numbers are the reference —
  // and the structure is not repeated in the rail, because it IS the main area.
  molecule: [{ id: "fragments" }],
};

/**
 * The rail's sections, top to bottom.
 *
 * **A selected step ADDS a section; it never replaces the rail.** That is the
 * unit's requirement stated as code: the rail used to be
 * `sidePanelFor(mode.id)` — one id, derived from the mode alone — so tile
 * settings and the molecule were mutually exclusive *by construction* and the
 * only way to see the other was to switch modes, which is the behaviour the
 * operator reported as "we are still very far from user-friendly".
 *
 * `tile` is appended rather than prepended so the rail's top does not move
 * under the user: the structure is the anchor, and the step's settings arrive
 * beneath it where a new section is expected to appear.
 *
 * **That ordering held and the OUTCOME was still wrong** (flowstack#1243 B):
 * beneath a structure taller than the viewport, "where a new section is
 * expected to appear" was 103px below the fold. The order is unchanged — see
 * {@link railSectionGivesUpHeight}, which makes room for the appended section
 * instead of moving it.
 *
 * The DAG carries `tile` unconditionally (it is in its `SIDE_BY_MODE` row)
 * because an empty tile panel there says *no node selected*, which is an
 * affordance — it names the thing clicking a node fills. In a visual mode
 * there is no graph on screen to click, so an empty tile section would be a
 * slot with no way to fill it.
 *
 * **`molecule` is gated on AVAILABILITY, not on the mode alone
 * (flowstack#1221 finding 1).** Only the DAG's rail carries it since VIS/V9 —
 * the torsion mode's reference is the scanned angle's conformer instead — and
 * the DAG is exactly where the operator asked for the ligand to survive a step
 * selection, so the gate stays. VIS/V6 put the structure in the DAG's rail
 * from the mode table only, so a run that never carried a structure-bearing
 * artifact got a *Molecule* panel saying "this run produced none" — a section
 * whose entire content is the news that it has no content. That is not the
 * same as the empty tile panel above: the tile panel names an action (click a
 * node), while a structure section on a run that has no structure names
 * nothing the user can do.
 *
 * The gate is `absent` ONLY. A `pending` molecule keeps its section, because
 * `pending` means the run may still produce one and the three-state rule
 * (`run-visual-views.ts`) is explicit that "not yet" must be offered and said
 * out loud rather than hidden — a section that appears mid-run is expected,
 * where a section that appears on a settled run would be a bug.
 *
 * The availability passed here must be the RUN's, not the selected step's —
 * see `runMoleculeAvailability` in `run-visual-views.ts`; a step with no
 * structure input must not make the section disappear.
 */
export function sidePanelsFor(
  mode: RunWorkspaceModeId,
  opts: { hasSelection: boolean; moleculeAvailability: RunVisualAvailability }
): RunWorkspaceSideId[] {
  const base = SIDE_BY_MODE[mode]
    .filter((section) => section.id !== "molecule" || opts.moleculeAvailability !== "absent")
    .map((section) => section.id);
  return opts.hasSelection && !base.includes("tile") ? [...base, "tile"] : [...base];
}

/**
 * **Does the rail survive while the MAIN pane holds the frame?**
 * (flowstack#1243 A.)
 *
 * "One pane takes the frame and the others stand down" is right for a READING
 * pane. Maximise the molecule or the plots and you went full-window to look at
 * that one thing; a rail beside it is a competing view, and #991's whole
 * complaint was that the thing you came for only ever got a share of the
 * window.
 *
 * The DAG is different in KIND, and that is the whole of this function. Its
 * rail is not a competing view — it is the **readout of the click you just
 * made**. `VIZ/U5.3` gave the graph a full-window mode because the operator
 * asked to work on the graph there, and node selection is the primary
 * interaction on that surface; a mode that disables the reason to enter it is
 * inverted. Measured in the expanded frame at `b945fdee`, both rail sections
 * reported `display:none, w:0, h:0` while the graph happily registered the
 * click, so selecting a node could never show anything.
 *
 * Stated as a rule over the MODE rather than as a general reversal of the
 * stand-down, and expressed here rather than inline in `RunWorkspace.tsx`, so
 * the next mode has to answer the question rather than inherit an answer.
 * `false` is the default a mode gets by being a reading pane; `dag` is the
 * exception and says why.
 *
 * **This is now one of TWO reasons a section survives** — see
 * {@link railSectionsUnderMaximisedMain}, which the workspace calls instead of
 * this. The other is inseparability (flowstack#1422), and the two are kept
 * apart because they are different claims: this one is about the MODE (its rail
 * is a readout, so all of it stays), inseparability is about one SECTION (the
 * main pane cannot be read without it). Collapsing them would make the DAG's
 * rail look like a pairing and the torsion pair look like a readout.
 */
export function railSurvivesMaximisedMain(mode: RunWorkspaceModeId): boolean {
  return mode === "dag";
}

/**
 * **Which rail sections are on screen while the MAIN pane holds the frame**
 * (flowstack#1422).
 *
 * The question the workspace actually asks, with the two reasons an answer can
 * be non-empty named separately above and below:
 *
 * - the mode's rail is the READOUT of the click you just made, so all of it
 *   stays ({@link railSurvivesMaximisedMain} — the DAG, and only the DAG);
 * - the section is INSEPARABLE from the main pane's content, so that one stays
 *   and the rest of the rail still stands down (`SIDE_BY_MODE` — the torsion
 *   mode's `structure`, and only it).
 *
 * Filtered against the LIVE `sections` rather than returning the table's rows,
 * because the rail is derived ({@link sidePanelsFor}) and a section that is not
 * rendered cannot be kept: a run whose molecule went `absent` mid-poll would
 * otherwise be told to hold a column open for a panel that is not there — the
 * same class of bug as a stale maximise (`RunWorkspace.tsx`'s `staleMaximise`).
 *
 * Empty is the ordinary answer, and it is what the whole per-pane design rests
 * on: a maximised reading pane is the window, not a share of it.
 */
export function railSectionsUnderMaximisedMain(
  mode: RunWorkspaceModeId,
  sections: readonly RunWorkspaceSideId[]
): RunWorkspaceSideId[] {
  if (railSurvivesMaximisedMain(mode)) return [...sections];
  return inseparableRailSections(mode, sections);
}

/**
 * The rail sections this mode's main pane cannot be read without — the
 * `inseparable` rows of `SIDE_BY_MODE`, narrowed to the rail as it currently is
 * (flowstack#1422).
 *
 * Separate from {@link railSectionsUnderMaximisedMain} because the workspace
 * asks it a DIFFERENT question: not "what is on screen in the frame" but "what
 * is this frame OF". A pair is one thing being expanded, so both its members'
 * controls name the pair and the frame announces both — where the DAG's
 * surviving rail is a readout beside the graph, and calling that frame "the DAG
 * with the molecule and the Tile settings" would claim a pairing that is not
 * there. Same reason the two survival rules stay apart.
 */
export function inseparableRailSections(
  mode: RunWorkspaceModeId,
  sections: readonly RunWorkspaceSideId[]
): RunWorkspaceSideId[] {
  const inseparable = new Set(
    SIDE_BY_MODE[mode].filter((section) => section.inseparable === true).map((s) => s.id)
  );
  return sections.filter((side) => inseparable.has(side));
}

/**
 * **Which rail sections give up height so the tile settings start on screen**
 * (flowstack#1243 B).
 *
 * Measured at `b945fdee`, `scrollY: 0`, viewport 679px: the Molecule section
 * ran 249→766 — it alone exceeds the viewport — and the Tile settings section
 * therefore started at 782, i.e. 103px BELOW the fold. A selection was
 * invisible without scrolling, and nothing on screen said it had happened.
 *
 * `sidePanelsFor` appends `tile` rather than prepending it so the rail's top
 * does not move under the user, and that reasoning is kept: **the fix is not a
 * reorder.** What changes is that a section ABOVE the tile settings gives up
 * height while a step is selected, so the readout of the click begins inside
 * the window. The alternatives were both worse for the thing VIS/V6 bought:
 * prepending moves the rail's top under the user (V6's own objection), and
 * scrolling the new section into view scrolls the GRAPH the user just clicked
 * off the top of the page — trading one unreachable panel for another.
 *
 * Gated on `hasSelection` so nothing is capped at rest: with no step selected
 * the structure keeps its full box, which is the state the rail spends most of
 * its life in.
 */
export function railSectionGivesUpHeight(args: {
  side: RunWorkspaceSideId;
  sections: readonly RunWorkspaceSideId[];
  hasSelection: boolean;
}): boolean {
  if (!args.hasSelection) return false;
  const tile = args.sections.indexOf("tile");
  if (tile < 0) return false;
  const self = args.sections.indexOf(args.side);
  return self >= 0 && self < tile;
}

/** Heading for the side panel — says what it is showing, never "detail". */
const SIDE_HEADINGS: Record<RunWorkspaceSideId, string> = {
  tile: "Tile settings",
  molecule: "Molecule",
  // The panel's own words, verbatim (`ScanGeometryPanel`): the section says
  // which structure this is — the one at the scanned angle, not the ligand —
  // and it must be the same sentence on both surfaces that draw it.
  structure: "Structure at this angle",
  fragments: "Torsion fits",
};

export function sideHeadingFor(side: RunWorkspaceSideId): string {
  return SIDE_HEADINGS[side];
}
